import { Modal } from "@mui/material";
import React, { useState } from "react";
import { DetailedRequisitionSerializer } from "../../recoil/types";
import { useAuthenticationHook } from "../../utils/hooks/useAuthenticationHook";
import { validateEmail } from "../../utils/hooks/helpers";
import { Button } from "../shared/Button/Button";
import { TextField } from "../shared/TextField/TextField";
import {
  downloadIntlBabyPeekPdf,
  shareIntlBabyPeekPdf,
  userReportIntlBabyPeek,
} from "./Services/UserReportService";
import {
  GeneralInfoProcessing,
  GeneralInfoText,
  GeneralInfoWrapper,
  IntlBabyPeekFeedback,
  IntlBabyPeekModalButtons,
  IntlBabyPeekModalDescription,
  IntlBabyPeekModalFields,
  IntlBabyPeekModalTitle,
  IntlBabyPeekModalWrapper,
  UserReportBabyPeekResultsWrapper,
  UserReportBabyPeekWrapper,
} from "./UserReport.styles";
import { ProcessingBabyPeekIcon } from "../../assets/iconComponents/ProcessingIcon";

interface IntlBabyPeekProps {
  reportData: DetailedRequisitionSerializer;
  setReportData: React.Dispatch<
    React.SetStateAction<DetailedRequisitionSerializer | undefined>
  >;
}

export const IntlBabyPeek = ({
  reportData,
  setReportData,
}: IntlBabyPeekProps): React.ReactElement => {
  const { auth, setLoggedOut } = useAuthenticationHook();
  const [ordering, setOrdering] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState(reportData.email || "");
  const [sharing, setSharing] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState("");
  const [queuedEmail, setQueuedEmail] = useState("");
  const busy = sharing || downloading;

  const closeModal = () => {
    setModalOpen(false);
    setRecipientEmail(reportData.email || "");
    setError("");
    setQueuedEmail("");
  };

  const orderBabyPeek = async () => {
    if (!auth?.token || !reportData.req_identifier) return;
    setOrdering(true);
    const report = await userReportIntlBabyPeek(
      reportData.req_identifier,
      auth.token,
      setLoggedOut,
    );
    if (report) setReportData(report);
    setOrdering(false);
  };

  const sharePdf = async () => {
    if (!auth?.token || !reportData.req_identifier) return;
    if (!validateEmail(recipientEmail)) {
      setError("Enter a valid recipient email address.");
      return;
    }

    setSharing(true);
    setError("");
    try {
      const response = await shareIntlBabyPeekPdf(
        reportData.req_identifier,
        recipientEmail,
        auth.token,
        setLoggedOut,
      );
      if (response.ok) {
        setQueuedEmail(recipientEmail);
      } else {
        setError("Unable to share the BabyPeek PDF. Please try again.");
      }
    } catch {
      setError("Unable to share the BabyPeek PDF. Please try again.");
    } finally {
      setSharing(false);
    }
  };

  const downloadPdf = async () => {
    if (!auth?.token || !reportData.req_identifier) return;

    setDownloading(true);
    setError("");
    try {
      const response = await downloadIntlBabyPeekPdf(
        reportData.req_identifier,
        auth.token,
        setLoggedOut,
      );
      if (!response.ok) throw new Error("Download request failed");

      const { download_url: downloadUrl, filename } = await response.json();
      const anchor = document.createElement("a");
      anchor.href = downloadUrl;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
    } catch {
      setError("Unable to download the BabyPeek PDF. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <GeneralInfoWrapper>
      <UserReportBabyPeekWrapper>
        {reportData.intl_baby_peek_status === "pending" ? (
          <>
            <GeneralInfoText id="req-billing-information">
              BABYPEEK
            </GeneralInfoText>
            {ordering ? (
              <GeneralInfoProcessing>
                <ProcessingBabyPeekIcon />
              </GeneralInfoProcessing>
            ) : (
              <Button
                label="ORDER BABYPEEK"
                onClick={orderBabyPeek}
                size="medium"
              />
            )}
          </>
        ) : (
          <UserReportBabyPeekResultsWrapper>
            <GeneralInfoText id="req-billing-information">
              BABYPEEK
            </GeneralInfoText>
            <div>Results available</div>
            <Button
              label="BABYPEEK PDF"
              onClick={() => setModalOpen(true)}
              size="medium"
            />
          </UserReportBabyPeekResultsWrapper>
        )}
      </UserReportBabyPeekWrapper>

      <Modal
        open={modalOpen}
        onClose={() => !busy && closeModal()}
        aria-labelledby="intl-babypeek-pdf-title"
        style={{
          alignItems: "center",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <IntlBabyPeekModalWrapper>
          <IntlBabyPeekModalTitle id="intl-babypeek-pdf-title">
            BabyPeek PDF
          </IntlBabyPeekModalTitle>
          {queuedEmail ? (
            <>
              <IntlBabyPeekFeedback role="status">
                The BabyPeek PDF has been queued for email to {queuedEmail}.
              </IntlBabyPeekFeedback>
              <IntlBabyPeekModalButtons>
                <Button label="CLOSE" onClick={closeModal} size="small" />
              </IntlBabyPeekModalButtons>
            </>
          ) : (
            <>
              <IntlBabyPeekModalDescription>
                Download the report or email it as an attachment.
              </IntlBabyPeekModalDescription>
              <IntlBabyPeekModalFields>
                <TextField
                  label="Recipient email"
                  type="email"
                  value={recipientEmail}
                  onChange={(event) => {
                    setRecipientEmail(event.target.value);
                    setError("");
                  }}
                  error={error.includes("valid recipient") ? error : undefined}
                />
              </IntlBabyPeekModalFields>
              {error && !error.includes("valid recipient") && (
                <IntlBabyPeekFeedback error role="alert">
                  {error}
                </IntlBabyPeekFeedback>
              )}
              <IntlBabyPeekModalButtons>
                <Button
                  label="CANCEL"
                  onClick={closeModal}
                  size="small"
                  backgroundColor="white"
                  textColor="greyDark"
                  borderColor="greyDark"
                  disabled={busy}
                />
                <Button
                  label="DOWNLOAD PDF"
                  onClick={downloadPdf}
                  size="small"
                  loading={downloading}
                  disabled={sharing}
                />
                <Button
                  label="EMAIL PDF"
                  onClick={sharePdf}
                  size="small"
                  loading={sharing}
                  disabled={downloading}
                />
              </IntlBabyPeekModalButtons>
            </>
          )}
        </IntlBabyPeekModalWrapper>
      </Modal>
    </GeneralInfoWrapper>
  );
};
